import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/packages");
    setPackages(data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!selected) return;
    try {
      const body = { ...selected };
      // features/add_ons ensure arrays
      if (typeof body.features === "string") body.features = body.features.split("\n").map((s) => s.trim()).filter(Boolean);
      if (!body.features) body.features = [];
      if (!body.add_ons) body.add_ons = [];
      body.price = Number(body.price) || 0;
      const { data } = await api.patch(`/admin/packages/${selected.id}`, body);
      toast.success("Package saved");
      setSelected(data);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const create = async () => {
    const { data } = await api.post("/admin/packages", {
      category_slug: "portraits",
      name: "New Package",
      price: 0,
      features: [],
      add_ons: [],
      order: 99,
      published: false,
    });
    toast.success("Package created");
    setSelected(data);
    load();
  };

  const del = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete ${selected.name}?`)) return;
    await api.delete(`/admin/packages/${selected.id}`);
    toast.success("Deleted");
    setSelected(null);
    load();
  };

  const featuresText = () => {
    if (!selected) return "";
    if (Array.isArray(selected.features)) return selected.features.join("\n");
    return selected.features || "";
  };

  return (
    <div data-testid="admin-packages">
      <div className="flex items-end justify-between">
        <div>
          <p className="label-mono opacity-60">Services</p>
          <h1 className="mt-2 font-editorial text-5xl md:text-6xl">Packages</h1>
        </div>
        <button className="btn-outline-ink" onClick={create} data-testid="package-new-btn">+ New Package</button>
      </div>

      <div className="mt-10 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-5 border border-ink/15 bg-white divide-y divide-ink/10 max-h-[70vh] overflow-y-auto">
          {packages.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelected(p)}
              className={`w-full text-left p-4 hover:bg-ink/5 ${selected?.id === p.id ? "bg-ink/5" : ""}`}
              data-testid={`package-row-${p.id}`}
            >
              <p className="font-editorial text-lg">{p.name} {p.most_popular ? " · ★" : ""}</p>
              <p className="label-mono opacity-60 mt-1">{p.category_slug} · R{p.price} · {p.published ? "Published" : "Hidden"}</p>
            </button>
          ))}
        </div>
        <div className="col-span-12 md:col-span-7 border border-ink/15 bg-white p-6 min-h-[400px]">
          {selected ? (
            <div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-mono opacity-60">Name</label>
                  <input className="editorial-input" value={selected.name} onChange={(e) => setSelected({ ...selected, name: e.target.value })} data-testid="package-name" />
                </div>
                <div>
                  <label className="label-mono opacity-60">Category</label>
                  <select className="editorial-select" value={selected.category_slug} onChange={(e) => setSelected({ ...selected, category_slug: e.target.value })}>
                    <option value="portraits">Portraits</option>
                    <option value="weddings">Weddings</option>
                    <option value="events">Events</option>
                    <option value="matric-balls">Matric Balls</option>
                    <option value="graduations">Graduations</option>
                  </select>
                </div>
                <div>
                  <label className="label-mono opacity-60">Price (ZAR)</label>
                  <input type="number" className="editorial-input" value={selected.price ?? 0} onChange={(e) => setSelected({ ...selected, price: e.target.value })} data-testid="package-price" />
                </div>
                <div>
                  <label className="label-mono opacity-60">Order</label>
                  <input type="number" className="editorial-input" value={selected.order ?? 0} onChange={(e) => setSelected({ ...selected, order: Number(e.target.value) })} />
                </div>
              </div>
              <div className="mt-4">
                <label className="label-mono opacity-60">Description</label>
                <textarea rows={3} className="editorial-textarea" value={selected.description || ""} onChange={(e) => setSelected({ ...selected, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-mono opacity-60">Duration</label>
                  <input className="editorial-input" value={selected.duration || ""} onChange={(e) => setSelected({ ...selected, duration: e.target.value })} />
                </div>
                <div>
                  <label className="label-mono opacity-60">Turnaround</label>
                  <input className="editorial-input" value={selected.turnaround || ""} onChange={(e) => setSelected({ ...selected, turnaround: e.target.value })} />
                </div>
              </div>
              <div className="mt-4">
                <label className="label-mono opacity-60">Features (one per line)</label>
                <textarea rows={8} className="editorial-textarea font-mono text-sm" value={featuresText()} onChange={(e) => setSelected({ ...selected, features: e.target.value.split("\n") })} data-testid="package-features" />
              </div>
              <div className="mt-4 flex flex-wrap gap-4 items-center">
                <label className="flex items-center gap-2 label-mono">
                  <input type="checkbox" checked={!!selected.most_popular} onChange={(e) => setSelected({ ...selected, most_popular: e.target.checked })} /> Most popular
                </label>
                <label className="flex items-center gap-2 label-mono">
                  <input type="checkbox" checked={!!selected.starting_from} onChange={(e) => setSelected({ ...selected, starting_from: e.target.checked })} /> "Starting from"
                </label>
                <label className="flex items-center gap-2 label-mono">
                  <input type="checkbox" checked={!!selected.published} onChange={(e) => setSelected({ ...selected, published: e.target.checked })} /> Published
                </label>
              </div>
              <div className="mt-8 flex gap-3">
                <button onClick={save} className="btn-solid" data-testid="package-save">Save</button>
                <button onClick={del} className="btn-outline-ink text-red-700" data-testid="package-delete">Delete</button>
              </div>
            </div>
          ) : (
            <p className="label-mono opacity-60">Select a package to edit.</p>
          )}
        </div>
      </div>
    </div>
  );
}
